import React from 'react';
import { Box, Container, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const APP_STORE_URL = 'https://apps.apple.com/app/rejection-tales/id6813563793';

const linkGroups = [
  {
    title: 'Explore',
    links: [
      { label: 'Home', to: '/' },
      { label: 'Tales', to: '/tales' },
      { label: 'Use Cases', to: '/use-cases' },
      { label: 'Groups', to: '/groups' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Resources', to: '/resources' },
      { label: 'Contact', to: '/contact' },
      { label: 'Support', to: '/support' },
      { label: 'Privacy', to: '/privacy' },
    ],
  },
  {
    title: 'Account',
    links: [
      { label: 'Login', to: '/auth' },
      { label: 'Post a Tale', to: '/create' },
      { label: 'Settings', to: '/settings' },
    ],
  },
];

const footerLinkStyle = {
  color: 'rgba(247, 246, 244, 0.78)',
  textDecoration: 'none',
  fontSize: '0.92rem',
  fontFamily: '"DM Sans", sans-serif',
  '&:hover': {
    color: '#F7F6F4',
    textDecoration: 'underline',
  },
};

const Footer = () => {
  const { isAuthenticated } = useAuth();
  const visibleLinkGroups = linkGroups.map((group) => ({
    ...group,
    links: group.links.filter((link) =>
      isAuthenticated
        ? !link.to || !['/about', '/use-cases'].includes(link.to)
        : true
    ),
  }));

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        background: 'linear-gradient(165deg, #4A3428 0%, #3A281F 100%)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        color: '#F7F6F4',
      }}
    >
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 3, md: 6 }}
          justifyContent="space-between"
        >
          <Box sx={{ maxWidth: 420 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Literata", Georgia, serif',
                fontWeight: 700,
                mb: 1,
              }}
            >
              Rejection Tales
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(247, 246, 244, 0.72)', lineHeight: 1.6, mb: 2 }}
            >
              A quiet place after a no. Share stories, sit with it, talk with Luna, and practice
              when you’re ready.
            </Typography>
            <Box
              component="a"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download Rejection Tales on the App Store"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                color: '#2A221C',
                bgcolor: '#F7F6F4',
                borderRadius: '10px',
                px: 1.75,
                py: 1,
                minHeight: 44,
                '&:hover': { bgcolor: '#fff' },
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                aria-hidden
                sx={{ width: 22, height: 22, fill: 'currentColor' }}
              >
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.22-1.98 1.08-3.13-1.05.04-2.31.7-3.06 1.58-.67.78-1.25 2.02-1.1 3.21 1.16.09 2.35-.63 3.08-1.66" />
              </Box>
              <Box sx={{ lineHeight: 1.1 }}>
                <Box component="span" sx={{ display: 'block', fontSize: '0.62rem', opacity: 0.75 }}>
                  Download on the
                </Box>
                <Box component="span" sx={{ display: 'block', fontSize: '0.98rem', fontWeight: 700 }}>
                  App Store
                </Box>
              </Box>
            </Box>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2.5, sm: 5 }}>
            {visibleLinkGroups.map((group) => (
              <Box key={group.title}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    mb: 1.25,
                    fontFamily: '"DM Sans", sans-serif',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    fontSize: '0.72rem',
                    color: 'rgba(247, 246, 244, 0.55)',
                  }}
                >
                  {group.title}
                </Typography>
                <Stack spacing={1}>
                  {group.links.map((link) =>
                    link.href ? (
                      <Box
                        key={link.href}
                        component="a"
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={footerLinkStyle}
                      >
                        {link.label}
                      </Box>
                    ) : (
                      <Box
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        sx={footerLinkStyle}
                      >
                        {link.label}
                      </Box>
                    )
                  )}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Stack>

        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.12)' }} />
        <Typography variant="caption" sx={{ color: 'rgba(247, 246, 244, 0.55)' }}>
          {new Date().getFullYear()} Rejection Tales. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
